import { Component, OnInit, OnDestroy, Input, Directive } from '@angular/core';
import { FormGroup, FormControl, AbstractControl } from "@angular/forms";
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { tap } from "rxjs/operators";
import { LoaderService } from "../../common/services/loader.service";
import { DynamicDialogService } from "../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../core/models/dialog.model";
import { SubHeaderService } from "../../core/services/sub-header.service";
import { CanDeactivateComponent } from "../../common/classes/can-deactivate.component";
import { AuthService } from "../../services/auth.service";
import { IUploadFile } from '../../common/services/document-management.service';
import { WorkflowData, ApplicationState, WorkflowFieldData, WorkflowFieldFile, WorkflowNote } from "../models/applications.models";
import { ApplicationPayment, ApplicationCreditAuthorization } from "../../payments/models/paymentItem.model";
import { ApplicationsService } from "../services/applications.service";
import { SidePanel } from "../../common/models/side-panel.model";
import { SidePanelService } from "../../common/services/side-panel.service";
import { ApplicationNotesService } from "../services/application-notes.service";
import { genId } from "../../core/services/genId";
import { NoteType } from "../../common/models/note-type.enum";
import { DocumentConfig } from "../../services/application-config.service";



@Directive()
export class ApplicationBaseComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    @Input() application: WorkflowData;
    delimiter: string = '|';
	doneForNowDialog: Dialog = new Dialog("Your application data has been saved.  Do you wish to continue working?", [new DialogButton("Yes", false, () => { return false; }), new DialogButton("No", true, () => { this.home(); })])
	doneForNowApplicationNumberDialog = new Dialog("", [new DialogButton("Yes", false, () => { return false; }), new DialogButton("No", true, () => { this.home(); })])
	protected readonly saveUnsubmittedMessage: string = "Your application has been saved for completion at a later date.  For future reference, your application number is [XXX]. Do you wish to continue working?";

    private _form: FormGroup = new FormGroup({});

    get form(): FormGroup {
        return this._form;
    }

    set form(value: FormGroup) {
        this._form = value;
        if (this.value$ != null) this.value$.unsubscribe();
        this.value$ = this.form.valueChanges.subscribe(
            () => {
                this.allowDeactivate.next(this.form.pristine);
            },
            (error) => {
                console.error('valueChanges', error);
            }
        );
    }

    public formReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public get formReady(): boolean {
        return this.formReady$.value;
    }

    public sidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Notes" }, { title: "Timeline" }],
        sections: [{ componentName: 'ApplicationNotes' }, { componentName: 'ApplicationTimeline' }]
    });

    protected arrayFields: string[] = [];
    protected numericFields: string[] = [];
    protected booleanFields: string[] = [];
    protected trueBooleanFields: string[] = [];
    protected dateFields: string[] = [];
    protected paymentFields: string[] = [];
    protected authorizationFields: string[] = [];
    protected fileFields: string[] = [];

    documentConfig: DocumentConfig;


    subscription: Subscription = new Subscription();

    value$: Subscription;

    constructor(protected _loader: LoaderService, _dialog: DynamicDialogService, protected subheaderService: SubHeaderService, _auth: AuthService, protected dataService: ApplicationsService,
        protected router: Router, protected sidePanelService: SidePanelService, protected notesService: ApplicationNotesService) {
        super(_dialog, _auth);
    }

    ngOnInit() {
        const noteAdded$: Subscription = this.notesService.noteSaved$
            .pipe(tap((note: WorkflowNote) => {

                if (this.application != null) {
                    if (this.application.workflowNotes == null) this.application.workflowNotes = [];

                    this.application.workflowNotes.push(note);

                }
            }))
            .subscribe(
                (note: WorkflowNote) => {
                    this.allowDeactivate.next(false);
                },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                }
        );
        this.subscription.add(noteAdded$);

    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
        if (this.value$ != null) this.value$.unsubscribe();
    }

    buildNewNote(fieldId: number = null): WorkflowNote {
        let id = genId();
        return {
            id: id,
            workflowId: this.application.id,
            fieldId: fieldId,
            createdDate: new Date(),
            createdBy: this.auth.User.displayName,
            createdById: this.auth.User.id,
            text: "",
            isDeleted: false,
            notePriorityId: 0,
            noteTypeId: 0,
            retentionPolicyId: id,
            retentionPolicyName: 'Indefinite'
        }
    }



    protected convertFileToFileFieldData(file: IUploadFile): WorkflowFieldFile {
        let converted: WorkflowFieldFile = {
            blobUri: file.blobUri,
            fileName: file.fileName,
            fileType: file.type,
            documentType: file.documentType,
            uploadedByOrganizationId: file.uploadedByOrganizationId,
            uploadedByUserId: file.uploadedByUserId
        } as WorkflowFieldFile;
        return converted;
    }



    protected convertFormToWorkflowFieldData(): WorkflowFieldData[] {
        let fields: WorkflowFieldData[] = this.application.workflowFieldDatas;
        if (fields == null) fields = [];
        Object.keys(this.form.controls).forEach(key => {
            let fullFieldName: string = key;
            let ctrl = this.form.get(key);
     
            if (ctrl instanceof FormControl) {
                let field: WorkflowFieldData = fields.find(f => f.formControlName == fullFieldName);
                let addField: boolean = field == null;
                if (addField) {
                    field = {
                        workflowId: this.application.id,
                        formControlName: fullFieldName
                    } as WorkflowFieldData;
                }
                field = this.setFieldValue(<FormControl>ctrl, field);
                if (addField) fields.push(field);
            }
            if (ctrl instanceof FormGroup) {
                Object.keys(ctrl.controls).forEach(subkey => {
                    let subctrl = this.form.get(key).get(subkey);
                    if (subctrl instanceof FormControl) {
                        fullFieldName = `${key}|${subkey}`;
                        let field: WorkflowFieldData = fields.find(f => f.formControlName == fullFieldName);
                        let addField: boolean = field == null;
                        if (addField) {
                            field = {
                                workflowId: this.application.id,
                                formControlName: fullFieldName
                            } as WorkflowFieldData;
                        }
                        field = this.setFieldValue(<FormControl>subctrl, field);
                        if (addField) fields.push(field);
                    }
                    if (subctrl instanceof FormGroup) {
                        Object.keys(subctrl.controls).forEach(bottomKey => {
                            let bottomCtrl = this.form.get(key).get(subkey).get(bottomKey);
                            if (bottomCtrl instanceof FormControl) {
                                fullFieldName = `${key}|${subkey}|${bottomKey}`;
                                let field: WorkflowFieldData = fields.find(f => f.formControlName == fullFieldName);
                                let addField: boolean = field == null;
                                if (addField) {
                                    field = {
                                        workflowId: this.application.id,
                                        formControlName: fullFieldName
                                    } as WorkflowFieldData;
                                }
                                field = this.setFieldValue(<FormControl>bottomCtrl, field);
                                if (addField) fields.push(field);
                            }
                            if (bottomCtrl instanceof FormGroup) {
                                Object.keys(bottomCtrl.controls).forEach(fourthKey => {
                                    let fourthCtrl = this.form.get(key).get(subkey).get(bottomKey).get(fourthKey);
                                    if (fourthCtrl instanceof FormControl) {
                                        fullFieldName = `${key}|${subkey}|${bottomKey}|${fourthKey}`;
                                        let field: WorkflowFieldData = fields.find(f => f.formControlName == fullFieldName);
                                        let addField: boolean = field == null;
                                        if (addField) {
                                            field = {
                                                workflowId: this.application.id,
                                                formControlName: fullFieldName
                                            } as WorkflowFieldData;
                                        }
                                        field = this.setFieldValue(<FormControl>fourthCtrl, field);
                                        if (addField) fields.push(field);
                                    }
                                   
                                });
                            }
                        });
                    }
                })
            }
        });



        return fields;
    }

    protected convertWorkflowFieldFileToIUploadFile(file: WorkflowFieldFile): IUploadFile {
        let converted: IUploadFile = {
            rawFile: null,
            completed: true,
            canBeDeleted: true,
            sentToOnbase: false,
            error: false,
            isUploading: false,
            waiting: false,
            percentage: 100,
            path: null,
            relativePath: null,
            fileName: file.fileName,
            type: file.fileType,
            uploadedByUserId: file.uploadedByUserId,
            uploadedByOrganizationId: file.uploadedByOrganizationId,
            blobUri: file.blobUri,
            documentType: file.documentType,
            id: file.id
        } as IUploadFile;
        return converted;
    }

    fieldHasNotes(field: number): boolean {
        if (this.application == null || this.application.workflowNotes == null) return false;
        let filteredNotes: WorkflowNote[] = [];
        if (this.auth.User.isInternal) filteredNotes = this.application.workflowNotes.filter(n => n.fieldId == field)
            .filter(n => n.noteTypeId != NoteType.Administrative || n.createdById == this.auth.User.id);
        else filteredNotes = this.application.workflowNotes.filter(n => n.fieldId == field && n.noteTypeId == NoteType.General);
        return filteredNotes.length > 0;
    }

    handleError(error): void {
        console.error(error);
        this.dialogService.showError();
    }

    public handleNotesRequested(fieldName: string): void {
        const field = this.application.workflowFieldDatas.find(f => f.formControlName == fieldName);
        if (field == null) return;   
        this.viewTimeline(field.id);
    }

    home(): void {
        this.router.navigate(["/home"]);
    }

    findMatchingControl(checkForm: FormGroup, formControlName: string): FormControl {
        let field: AbstractControl = null;
        let parts = formControlName.split('|');
        parts.forEach(n => {
            if (n.startsWith('|')) n = n.substring(1, n.length - 1);
            else if (n.endsWith('|')) n = n.substring(0, n.length - 2);
        });
        switch (parts.length) {
            case 1:
                field = checkForm.get(parts[0]);
                break;
            case 2:
                field = checkForm.get(parts[0]).get(parts[1]);
                break;
            case 3:
                field = checkForm.get(parts[0]).get(parts[1]).get(parts[2]);
                break;
            case 4:
                field = checkForm.get(parts[0]).get(parts[1]).get(parts[2]).get(parts[3]);
                break;
        }
        return <FormControl>field;
    }

    restoreFormData(pendingForm: FormGroup): FormGroup {
        if (this.application.workflowFieldDatas != null && this.application.workflowFieldDatas.length > 0) {
            this.application.workflowFieldDatas.forEach(f => {
                let name: string = f.formControlName;
                if (name != null) {
                    let field: AbstractControl = this.findMatchingControl(pendingForm, name);
                    if (field != null) {
                        if (this.booleanFields.includes(name)) {
                            if (f.booleanData != null) field.setValue(new Boolean(f.booleanData).toString());
                            else field.setValue(null);
                        }
                        else if (this.trueBooleanFields.includes(name)) {
                            if (f.booleanData != null) field.setValue(f.booleanData)
                            else field.setValue(null);
                        }
                        else if (this.numericFields.includes(name)) field.setValue(f.numericData);
                        else if (this.fileFields.includes(name)) {
                            //convert stored field back to IUpload file, then set value
                            let files: IUploadFile[] = [];
                            f.workflowFieldFiles.forEach(file => {
                                files.push(this.convertWorkflowFieldFileToIUploadFile(file));
                            })
                            field.setValue(files);
                        }
                        else if (this.dateFields.includes(name)) field.setValue(f.dateData);
                        else if (this.paymentFields.includes(name)) {
                            field.setValue(f.applicationPayments);
                        }
                        else if (this.authorizationFields.includes(name)) {
                            field.setValue(f.applicationCreditAuthorization);
                        }
                        else if (this.arrayFields.includes(name)) {
                            let valueAsArray = f.stringData.split(this.delimiter);
                            valueAsArray = valueAsArray.filter(v => v != null && v.length > 0);
                            field.setValue(valueAsArray);
                        }
                        else field.setValue(f.stringData);
                        if (this.application.applicationState == ApplicationState.Corrections && this.fieldHasNotes(f.id) == false) {
                            field.disable();
                        }
                    }
                }
            })
        }
        return pendingForm;
    }

    save(confirmContinue: boolean): void {
        this._loader.toggleLoader(true);
        let save$ = this.saveProgress().pipe(
            tap((data: WorkflowData) => {
                this.application = data;
            })
        ).subscribe(
            () => {
                this._loader.toggleLoader(false);
                this.form.markAsPristine();
                if (confirmContinue) {
                    this.doneForNowDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.doneForNowDialog);
                }
                this.allowDeactivate.next(true);
            },
            (error) => {
                console.error(error);
                this._loader.toggleLoader(false);
                this.dialogService.showError();
            }
        );
        this.subscription.add(save$);
    }

    saveProgress(): Observable<WorkflowData> {
        this.application.workflowFieldDatas = this.convertFormToWorkflowFieldData();
        this.setAuthorizationRecordIds();
        let save$: Observable<WorkflowData> = this.dataService.saveApplication(this.application);
        return save$;
    }

    setAuthorizationRecordIds() : void {
    }

    setFieldValue(ctrl: FormControl, field: WorkflowFieldData): WorkflowFieldData {
        let value: any = ctrl.value;
        if (this.numericFields.includes(field.formControlName)) {
            value = parseFloat(value);
            field.numericData = value;
        } else if (this.booleanFields.includes(field.formControlName) || this.trueBooleanFields.includes(field.formControlName)) {
            field.booleanData = value;
        } else if (this.fileFields.includes(field.formControlName)) {
            let updatedFiles: WorkflowFieldFile[] = [];
            let storedField = this.application.workflowFieldDatas.find(f => f.formControlName == field.formControlName);
   
            (value as IUploadFile[]).forEach(file => {
                let converted = this.convertFileToFileFieldData(file);
                if (storedField != null) {
                    converted.fieldId = storedField.id;
                    let match = storedField.workflowFieldFiles.find(m => m.fileName == converted.fileName);
                    if (match != null) converted.id = match.id;
                }
                updatedFiles.push(converted);
            });
            field.workflowFieldFiles = updatedFiles;
        }
        else if (this.dateFields.includes(field.formControlName)) {
            field.dateData = value;
        }
        else if (this.paymentFields.includes(field.formControlName)) {
            let updatedPayments: ApplicationPayment[] = [];
            let storedField = this.application.workflowFieldDatas.find(f => f.formControlName == field.formControlName);
            if (value != null) {
                (value as ApplicationPayment[]).forEach(payment => {
                    if (storedField != null) payment.workflowFieldId = storedField.id;
                    if (this.documentConfig != null) {
                        if (payment.receiptDocumentType == null || payment.receiptDocumentType == "") payment.receiptDocumentType = this.documentConfig.receipt;
                        if (payment.invoiceDocumentType == null || payment.invoiceDocumentType == "") payment.invoiceDocumentType = this.documentConfig.invoice;
                    }
                    updatedPayments.push(payment);
                });
            }
            field.applicationPayments = updatedPayments;
        }
        else if (this.authorizationFields.includes(field.formControlName)) {
            let storedField = this.application.workflowFieldDatas.find(f => f.formControlName == field.formControlName);
            let auth: ApplicationCreditAuthorization = null;
            if (value != null) {
                auth = <ApplicationCreditAuthorization>value;
                if (storedField != null) auth.id = storedField.id;
            }
            field.applicationCreditAuthorization = auth;
        }
        else if (this.arrayFields.includes(field.formControlName)) {
            let stringValue = "";
            (<string[]>value).forEach(s => {
                if (stringValue.length == 0) stringValue = s;
                else stringValue = `${stringValue}${this.delimiter}${s}`;
            });
            field.stringData = stringValue;
        }
        else {
            field.stringData = value;
        }
        return field;
    }

    unauthorized(): void {
        this.router.navigate(['/home/unauthorized']);
    }

    sortNotes(notes: WorkflowNote[]): WorkflowNote[] {
        let parented = notes.filter(f => f.parentNoteId);
        let unparented = notes.filter(f => !f.parentNoteId);
        parented.forEach(note => {
            const parent = unparented.find(n => n.id == note.parentNoteId);
            if (parent) {
                if (parent.childNotes.includes(note) == false) {
                    parent.childNotes.push(note);
                }
            }
        });

        return unparented;
    }

    getFilteredNotes(fieldName: string): WorkflowNote[] {
        if (this.application == null) return [];
        if (this.application.workflowNotes == null) return [];
        if (this.application.workflowFieldDatas == null) return [];
        let field = this.application.workflowFieldDatas.find(f => f.formControlName == fieldName);
        if (field == null) return [];

        let selectedNotes = (this.application.workflowNotes == null)
            ? []
            : this.application.workflowNotes;

        let unparented = this.sortNotes(selectedNotes);

        if (this.auth.User.isInternal) return unparented.filter(f => f.fieldId == field.id).filter(n => n.noteTypeId != NoteType.Administrative || n.createdById == this.auth.User.id);
        else return unparented.filter(f => f.fieldId == field.id).filter(n => n.noteTypeId == NoteType.General);
    }


    viewTimeline(fieldId: number = null): void {
        let selectedNotes = (this.application.workflowNotes == null)
            ? []
            : this.application.workflowNotes; 

        if (fieldId == null) {
            selectedNotes = this.sortNotes(selectedNotes);
        }

        else {
            let fieldNote = this.application.workflowFieldDatas.find(n => n.id == fieldId)
            selectedNotes = this.getFilteredNotes(fieldNote.formControlName);
        }

        this.notesService.updateHistory(this.application.flowHistories);
 
        let note = this.buildNewNote(fieldId);
      
        this.notesService.add(note);
        this.notesService.updateNoteList(selectedNotes);
        
        this.sidePanelService.newSidePanel(this.sidePanel);
        this.sidePanelService.toggleSidePanel(true);
    }
}